// Keeps an open picker dropdown clear of the on-screen keyboard.
//
// Ported from VABS (`apps/pwa/lib/picker-keyboard-scroll.ts`), which shipped this
// against the same two platforms and settled a number of things the hard way.
// Those conclusions are kept as comments here rather than rediscovered.
//
// Radix positions the popover for us and will happily FLIP it above the trigger
// when there's no room below. We turn that off (`side="bottom"
// avoidCollisions={false}`) because flipping contradicts the geometry in this
// module: every calculation below measures the list as hanging BELOW the trigger,
// so a flipped list occupies space the scroll math still treats as needing to be
// cleared — the form gets pushed up for a list that was never down there. One
// consistent direction, one consistent measurement; the height cap absorbs a
// short screen instead.
//
// The keyboard's top edge comes from `window.visualViewport`, whose height and
// offset shrink when the keyboard is up on both iOS WKWebView and Android
// WebView. That is the only cross-platform signal available to web code — there
// is no DOM event for "keyboard height" — and it holds whether Atlas is running
// in the native shell (which animates the host iframe's height on iOS) or in
// plain Chrome on Android (where the WebView resizes the layout viewport).

/** Gap between the bottom of the list and the top of the keyboard. */
export const BOTTOM_GAP = 16;
/** Never scroll so far that the field is jammed against the top of the viewport;
 *  leave this much above it. */
export const TOP_GAP = 12;
/** The popover's own offset from its trigger — Radix `sideOffset`, which
 *  `PopoverContent` defaults to 4. Keep the two in step. */
export const LIST_GAP = 4;
/** Dead zone: a picker already sitting where it needs to be stays put. Without
 *  this, a few pixels of overflow (or a keyboard height that wobbles by a point
 *  as accessory bars settle) would nudge the form for no visible benefit. */
export const MIN_SCROLL = 8;

/**
 * Dead zone for a bare field — no dropdown hanging below it — which has to be
 * much wider than `MIN_SCROLL`.
 *
 * The browser does its own scroll-into-view when the keyboard opens, and it stops
 * as soon as the field is merely visible: bottom edge flush against the keyboard,
 * no gap. We aim `LIST_GAP + BOTTOM_GAP` higher than that, so a field the browser
 * has *already* parked still reads as 20px of overflow and gets nudged again —
 * two movements for one focus, which is the glitch.
 *
 * A bare input sitting up to one gap low is indistinguishable to the eye, so the
 * nudge buys nothing. For a picker the same 20px matters, because it's the
 * difference between the last option being visible and being clipped — hence two
 * different dead zones rather than one compromise.
 *
 * Must be strictly GREATER than `LIST_GAP + BOTTOM_GAP`: the guard is
 * `scrollBy < minScroll`, so a dead zone of exactly the gap still lets a
 * flush-parked field through by one pixel. The extra 4 also absorbs the
 * fractional rects `getBoundingClientRect` returns.
 */
export const FIELD_MIN_SCROLL = LIST_GAP + BOTTOM_GAP + 4;

export interface PickerScrollInput {
  /** Viewport-relative top of the trigger. */
  wrapperTop: number;
  /**
   * Viewport-relative top of the whole FIELD — the trigger plus the label above
   * it. Defaults to `wrapperTop` when the picker has no label.
   *
   * `TOP_GAP` is measured from here, and that is the difference between "the
   * trigger stays on screen" and "the field stays on screen". Clamping to the
   * trigger alone leaves the label inside the region the scroll is free to
   * consume, so the form scrolls until the label is gone and the user is looking
   * at a search box with no idea what it's searching — which is exactly what a
   * driver picker with the "Driver" label scrolled away looks like.
   */
  anchorTop?: number;
  /** Viewport-relative bottom of the trigger; the list starts just below. */
  wrapperBottom: number;
  /** Rendered height of the open list. */
  listHeight: number;
  /** Top of the visible (unobscured) viewport. */
  viewportTop: number;
  /** Bottom of the visible viewport, i.e. the top of the keyboard. */
  viewportBottom: number;
  /** How much further the scroll container can still scroll down. */
  maxScroll: number;
  /**
   * Movement below this counts as "already in place". Defaults to `MIN_SCROLL`,
   * which decides whether to move at all; the per-frame follow loop passes 0 so
   * it can close the last few pixels once moving.
   */
  minScroll?: number;
}

export interface PickerScrollResult {
  /** Pixels to scroll the container down by. Never negative. */
  scrollBy: number;
  /**
   * Extra bottom padding the scroll content needs before `scrollBy` is actually
   * reachable (a picker near the end of a form has nothing left to scroll). Zero
   * when the container can already scroll far enough.
   */
  padBottom: number;
}

export function computePickerScroll(input: PickerScrollInput): PickerScrollResult {
  const {
    wrapperTop,
    anchorTop = wrapperTop,
    wrapperBottom,
    listHeight,
    viewportTop,
    viewportBottom,
    maxScroll,
    minScroll = MIN_SCROLL,
  } = input;

  // How far the list currently overflows past the keyboard's top edge.
  const overflow = wrapperBottom + LIST_GAP + listHeight + BOTTOM_GAP - viewportBottom;
  if (overflow <= 0) return { scrollBy: 0, padBottom: 0 };

  // Scrolling past this would push the field — label included — off the top.
  const headroom = Math.max(0, anchorTop - (viewportTop + TOP_GAP));
  const scrollBy = Math.min(overflow, headroom);
  if (scrollBy < minScroll || scrollBy <= 0) return { scrollBy: 0, padBottom: 0 };

  return { scrollBy, padBottom: Math.max(0, scrollBy - Math.max(0, maxScroll)) };
}

/** Matches the picker's own `max-h-[20rem]` cap; the list never wants to be
 *  taller. */
export const MAX_LIST_HEIGHT = 320;
/** Below this a list is too short to be worth showing, so it's allowed to run
 *  under the keyboard rather than collapse to a sliver. */
export const MIN_LIST_HEIGHT = 160;

/**
 * How tall the list may be once the form has scrolled.
 *
 * Radix publishes `--radix-popover-content-available-height`, which the picker
 * already uses as a CSS cap. This is the same idea computed against the *post-
 * scroll* position: Radix measures where the popover is NOW, so on its own it
 * shrinks the list to fit a viewport we're about to make more room in.
 */
export function computeListMaxHeight(input: {
  /** Bottom of the trigger, before scrolling. */
  wrapperBottom: number;
  /** Distance the form is about to scroll (0 if it cannot). */
  scrollBy: number;
  viewportBottom: number;
}): number {
  const listTop = input.wrapperBottom - input.scrollBy + LIST_GAP;
  const available = input.viewportBottom - listTop - BOTTOM_GAP;
  return Math.max(MIN_LIST_HEIGHT, Math.min(MAX_LIST_HEIGHT, Math.floor(available)));
}

/**
 * The highest point the picker's field is allowed to be scrolled to.
 *
 * The visual viewport alone is wrong inside a dialog: the picker scrolls within a
 * panel whose top edge sits below a pinned header, so clamping to the screen lets
 * the field slide up underneath that header and out of the scrolling box
 * entirely. Whichever boundary is LOWER is the real one.
 *
 * `containerTop` is null when there's no scrolling ancestor to clamp against, in
 * which case the viewport is the only boundary there is.
 */
export function effectiveViewportTop(
  viewportTop: number,
  containerTop: number | null,
): number {
  if (containerTop === null) return viewportTop;
  return Math.max(viewportTop, containerTop);
}

/** Marks chrome that stays pinned to the top of a scroll container, so the scroll
 *  maths knows not to hide a field behind it. Put it on the same element as the
 *  `sticky top-0` classes. */
export const STICKY_TOP_ATTR = 'data-sticky-top';

interface Edges { top: number; bottom: number }
const measureEdges = (el: HTMLElement): Edges => {
  const r = el.getBoundingClientRect();
  return { top: r.top, bottom: r.bottom };
};

/**
 * The highest point inside a scroll container that's actually visible.
 *
 * Usually the container's own top edge — but not when the container holds sticky
 * chrome. A full-screen dialog scrolls as one piece with its title bar pinned
 * inside it via `position: sticky`, so the container's top is ABOVE that bar and
 * clamping to it lets a field scroll up underneath the title, label first. (The
 * label going missing is the tell: the trigger is still visible, sitting flush
 * under the header, and the user can no longer see which field they're editing.)
 *
 * Anything tagged `data-sticky-top` therefore raises the boundary to its own
 * bottom edge. `measure` is injected so this can be tested without a layout
 * engine.
 */
export function containerContentTop(
  root: HTMLElement | null,
  measure: (el: HTMLElement) => Edges = measureEdges,
): number | null {
  if (!root) return null;
  const top = measure(root).top;
  const chrome = root.querySelector<HTMLElement>(`[${STICKY_TOP_ATTR}]`);
  if (!chrome) return top;
  // `max` because the chrome may itself be scrolled out of the way in a
  // container that doesn't actually pin it at this width.
  return Math.max(top, measure(chrome).bottom);
}

/** How far above the trigger the anchor may reach. A label is one or two lines
 *  plus a gap; beyond that the markup is something this walk doesn't understand,
 *  and anchoring to it would shrink the headroom so far that the list would be
 *  left under the keyboard — trading a hidden label for a hidden list. */
export const MAX_ANCHOR_RISE = 96;

/** Levels of wrapper the walk will climb looking for the field's label. Two
 *  covers the shapes in this app: the label is either the trigger's own previous
 *  sibling (`<div><Label/><LookupField/></div>`, the common case — Radix's
 *  Popover.Root renders no element, so the trigger really is that direct child),
 *  or a sibling of the div a wrapper component puts the picker in. */
const MAX_ANCHOR_DEPTH = 2;

/**
 * Top of the field a picker belongs to: the trigger, plus the label above it,
 * wherever the markup happens to put that label.
 *
 * At each level it walks BACKWARD over preceding siblings, taking `<label>`
 * elements (what the shadcn `Label` renders) and anything tagged
 * `data-picker-anchor`, and stopping at the first sibling that is neither — that
 * one belongs to the PREVIOUS field, which nobody promised to keep on screen.
 * Then it climbs one wrapper and repeats.
 *
 * Bounded twice over: `MAX_ANCHOR_DEPTH` levels and `MAX_ANCHOR_RISE` pixels. An
 * unfamiliar layout degrades to "protect a bit less", never to "refuse to
 * scroll".
 *
 * `measure` is injected so the walk can be tested without a layout engine.
 */
export function fieldAnchorTop(
  wrapper: HTMLElement,
  measure: (el: HTMLElement) => number = (el) => el.getBoundingClientRect().top,
): number {
  const wrapperTop = measure(wrapper);
  let top = wrapperTop;
  let node: HTMLElement | null = wrapper;

  for (let depth = 0; node && depth <= MAX_ANCHOR_DEPTH; depth++) {
    for (let sib = node.previousElementSibling; sib; sib = sib.previousElementSibling) {
      // tagName, not instanceof: cheap, and it doesn't care which realm or
      // document the element came from.
      if (sib.tagName !== 'LABEL' && !sib.hasAttribute('data-picker-anchor')) break;
      top = Math.min(top, measure(sib as HTMLElement));
    }
    // Found the label — climbing further would start collecting the section
    // heading above the field.
    if (top < wrapperTop) break;
    node = node.parentElement;
  }

  return Math.max(top, wrapperTop - MAX_ANCHOR_RISE);
}

export interface VisibleViewport {
  top: number;
  bottom: number;
}

/**
 * The part of the viewport not covered by the keyboard, measured live — this is
 * read on every `visualViewport` change while a list is open, so it tracks the
 * keyboard as it slides rather than predicting where it will end up. Falls back
 * to the full window where `visualViewport` is missing, which simply means "no
 * keyboard known".
 */
export function visibleViewport(): VisibleViewport {
  if (typeof window === 'undefined') return { top: 0, bottom: 0 };
  const vv = window.visualViewport;
  if (!vv) return { top: 0, bottom: window.innerHeight };
  return { top: vv.offsetTop, bottom: vv.offsetTop + vv.height };
}

/**
 * The element that actually scrolls a picker into view.
 *
 * A picker inside a dialog lives in a portal, inside a `position: fixed` panel
 * that does its own scrolling: scrolling the page behind it would move nothing on
 * screen. So walk up to the nearest scrollable ancestor and stop at any
 * fixed-position element, since nothing outside one can move its contents.
 *
 * Returns null when a fixed container has no scrollable ancestor inside it —
 * better to leave the picker alone than to scroll the wrong thing.
 */
export function scrollContainerOf(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    // Deliberately not gated on scrollHeight > clientHeight: a panel with room to
    // spare still becomes scrollable once the spacer is added.
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') return node;
    if (style.position === 'fixed') return null;
  }
  return document.scrollingElement as HTMLElement | null;
}
